import React, { useEffect, useState, useRef } from 'react';
import { CheckIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { ServiceCard } from '../components/servis/ServiceCard';
import { ServisRequestForm } from '../components/servis/ServisRequestForm';
import { PartnerForm } from '../components/servis/PartnerForm';
import { servisers } from '../data/servisers';
import { useTranslation } from '../lib/i18n';
export function ServicePage() {
  const {
    lang
  } = useTranslation();
  const en = lang === 'en';
  const ru = lang === 'ru';
  const requestFormRef = useRef<HTMLDivElement>(null);
  const [sectionsVisible, setSectionsVisible] = useState<Record<string, boolean>>({});
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.target.id) {
          setSectionsVisible(prev => ({
            ...prev,
            [entry.target.id]: true
          }));
        }
      });
    }, {
      threshold: 0.15
    });
    document.querySelectorAll('[data-animate]').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
  const scrollToRequestForm = () => {
    requestFormRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const benefits = en ? ['Technical training', 'Access to spare parts', 'Manufacturer support', 'Inclusion in the official service network', 'Regional exclusivity (by agreement)'] : ru ? ['Техническое обучение', 'Доступ к запасным частям', 'Поддержка производителя', 'Включение в официальную сервисную сеть', 'Региональный эксклюзив (по согласованию)'] : ['Tehničku obuku', 'Pristup rezervnim delovima', 'Podršku proizvođača', 'Uvrštavanje u zvaničnu servisnu mrežu', 'Regionalnu ekskluzivu (po dogovoru)'];
  const requirements = en ? ['Registered shop or company', 'Experience working with electrical or industrial equipment', 'Field vehicle', 'Basic tools and diagnostic equipment', 'Readiness for field work'] : ru ? ['Зарегистрированный магазин или компания', 'Опыт работы с электрическим или промышленным оборудованием', 'Полевой автомобиль', 'Базовые инструменты и диагностическое оборудование', 'Готовность к полевым работам'] : ['Registrovana radnja ili firma', 'Iskustvo u radu sa elektro ili industrijskom opremom', 'Terensko vozilo', 'Osnovni alat i dijagnostička oprema', 'Spremnost za terenski rad'];
  return <main className="min-h-screen bg-dark-bg" data-id="element-1057" data-mp="3gefmw0jd1kf1">
      {/* Hero */}
      <section className="py-10 md:py-14 px-4 sm:px-6 lg:px-8 border-b border-dark-border" data-id="element-1058" data-mp="1vvbqqp8bc8mr">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1059" data-mp="20tww132f9ii2">
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4" data-id="element-1060" data-mp="20tdc5gt7ok1e">
            {en ? 'Authorized service and ' : ru ? 'Авторизованный сервис и ' : 'Ovlašćeni servis i '}
            <span className="text-brand-yellow" data-id="element-1061" data-mp="1d4u3v1a5w79b">
              {en ? 'technical support' : ru ? 'техническая поддержка' : 'tehnička podrška'}
            </span>
          </h1>
          <p className="text-lg text-gray-300" data-id="element-1062" data-mp="137ppvoqgzi3l">
            {en ? 'Expert service, available spare parts, and support across Serbia.' : ru ? 'Экспертный сервис, доступные запасные части и поддержка по всей Сербии.' : 'Stručan servis, dostupni rezervni delovi i podrška širom Srbije.'}
          </p>
        </div>
      </section>

      {/* Servisna mreža */}
      <section className="py-10 md:py-14 px-4 sm:px-6 lg:px-8 border-b border-dark-border" data-id="element-1063" data-mp="12ghk0uo411as">
        <div className="max-w-6xl mx-auto" data-id="element-1064" data-mp="xipgt4i0lsra">
          <h2 id="mreza" data-animate className={`text-3xl md:text-4xl font-black text-white mb-8 text-center transition-all duration-600 ${sectionsVisible.mreza ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} data-id="element-1065" data-mp="tgcbuib3dyvk">
            {en ? 'Service ' : ru ? 'Сервисная ' : 'Servisna '}
            <span className="text-brand-yellow" data-id="element-1066" data-mp="eqn6eavyv8ph">
              {en ? 'network' : ru ? 'сеть' : 'mreža'}
            </span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-1067" data-mp="9qismsmg0s2y">
            {servisers.map((serviser, index) => <ServiceCard key={serviser.id} serviser={serviser} index={index} data-id="element-1068" data-mp="rx9av95d914" data-mpi={index} />)}
          </div>
        </div>
      </section>

      {/* Online zahtev za servis */}
      <section ref={requestFormRef} className="py-10 md:py-14 px-4 sm:px-6 lg:px-8 border-b border-dark-border bg-dark-surface/50" data-id="element-1069" data-mp="27f4g5lmo782t">
        <div className="max-w-3xl mx-auto" data-id="element-1070" data-mp="2hedesrshdavi">
          <h2 id="zahtev" data-animate className={`text-3xl md:text-4xl font-black text-white mb-4 text-center transition-all duration-600 ${sectionsVisible.zahtev ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} data-id="element-1071" data-mp="2hfojquxnwoo7">
            {en ? 'Online service ' : ru ? 'Онлайн-запрос на ' : 'Online zahtev za '}
            <span className="text-brand-yellow" data-id="element-1072" data-mp="1xp30ni8duxzi">
              {en ? 'request' : ru ? 'обслуживание' : 'servis'}
            </span>
          </h2>
          <p className="text-gray-300 text-center mb-10" data-id="element-1073" data-mp="15zggeraep9bh">
            {en ? 'Fill out the form below and our service team will contact you as soon as possible.' : ru ? 'Заполните форму ниже, и наша сервисная служба свяжется с вами в кратчайшие сроки.' : 'Popunite formu ispod i naša servisna služba će vas kontaktirati u najkraćem roku.'}
          </p>
          <div className="bg-dark-bg border border-dark-border rounded-xl p-6 md:p-8" data-id="element-1074" data-mp="2n1s8cqmnn0b4">
            <ServisRequestForm data-id="element-1075" data-mp="2i1w4qshvdaw3" />
          </div>
        </div>
      </section>

      {/* Postani serviser */}
      <section className="py-10 md:py-14 px-4 sm:px-6 lg:px-8 border-b border-dark-border" data-id="element-1076" data-mp="1uc1sod5djqcw">
        <div className="max-w-6xl mx-auto" data-id="element-1077" data-mp="1pea0di0ti286">
          <h2 id="partner" data-animate className={`text-3xl md:text-4xl font-black text-white mb-4 text-center transition-all duration-600 ${sectionsVisible.partner ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} data-id="element-1078" data-mp="1pfl5bl601g0v">
            {en ? 'Become a D-TECH ' : ru ? 'Станьте D-TECH ' : 'Postanite D-TECH '}
            <span className="text-brand-yellow" data-id="element-1079" data-mp="3atfsmppevd4f">
              {en ? 'authorized service partner' : ru ? 'авторизованным сервисным партнером' : 'ovlašćeni serviser'}
            </span>
          </h2>
          <p className="text-gray-300 text-center mb-10 max-w-3xl mx-auto" data-id="element-1080" data-mp="38dzjcny75nc6">
            {en ? 'We are expanding our service network and looking for technically qualified partners to work with D-TECH systems. We provide technical training, support, and availability of spare parts.' : ru ? 'Мы расширяем нашу сервисную сеть и ищем технически квалифицированных партнеров для работы с системами D-TECH. Мы предоставляем техническое обучение, поддержку и доступность запасных частей.' : 'Širimo servisnu mrežu i tražimo tehnički osposobljene partnere za rad sa D-TECH sistemima. Obezbeđujemo tehničku obuku, podršku i dostupnost rezervnih delova.'}
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-1081" data-mp="sndbw20y4jum">
            {/* Left column */}
            <div data-id="element-1082" data-mp="2oqjg1c92fzym">
              <h3 className="text-xl font-bold text-white mb-4" data-id="element-1083" data-mp="2jxqc2qlf8vnl">
                {en ? 'What we ' : ru ? 'Что мы ' : 'Šta '}
                <span className="text-brand-yellow" data-id="element-1084" data-mp="2fvlh9oj8lszd">
                  {en ? 'offer:' : ru ? 'предлагаем:' : 'nudimo:'}
                </span>
              </h3>
              <ul className="space-y-3" data-id="element-1085" data-mp="2av7ae9tdgp2x">
                {benefits.map((benefit, index) => <li key={index} className="flex items-start gap-3 text-gray-300" data-id="element-1086" data-mp="37l6risnl8vx4" data-mpi={index}>
                    <CheckIcon className="w-5 h-5 text-brand-yellow flex-shrink-0 mt-0.5" data-id="element-1087" data-mp="2yltjm4f44fei" />
                    <span data-id="element-1088" data-mp="2to1hkhkr7cwl">{benefit}</span>
                  </li>)}
              </ul>
            </div>

            {/* Right column */}
            <div className="bg-dark-surface border border-dark-border rounded-xl p-6" data-id="element-1089" data-mp="1n8r3fagxdruv">
              <h3 className="text-xl font-bold text-white mb-5" data-id="element-1090" data-mp="1i6rznzh9wsxe">
                {en ? 'Apply for service ' : ru ? 'Заявка на сервисное ' : 'Prijava za servisnu '}
                <span className="text-brand-yellow" data-id="element-1091" data-mp="1i2luaiyg2552">
                  {en ? 'partnership' : ru ? 'партнерство' : 'saradnju'}
                </span>
              </h3>
              <PartnerForm data-id="element-1092" data-mp="1mun663rp9tuk" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-14 px-4 sm:px-6 lg:px-8 bg-dark-surface border-t border-dark-border" data-id="element-1093" data-mp="1qx096kf2bdnn">
        <div className="max-w-3xl mx-auto text-center" data-id="element-1094" data-mp="1vvtrcq5ansu5">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4" data-id="element-1095" data-mp="20uer6ogmwai2">
            {en ? 'Need an ' : ru ? 'Нужно ' : 'Potrebna vam je '}
            <span className="text-brand-yellow" data-id="element-1096" data-mp="25pu1x7jchqa9">
              {en ? 'intervention?' : ru ? 'вмешательство?' : 'intervencija?'}
            </span>
          </h2>
          <p className="text-lg text-gray-300 mb-8" data-id="element-1097" data-mp="beyljj1x56wx">
            {en ? 'Contact the responsible service technician or submit an online request.' : ru ? 'Свяжитесь с ответственным сервисным специалистом или отправьте онлайн-запрос.' : 'Kontaktirajte nadležnog servisera ili pošaljite zahtev online.'}
          </p>
          <Button variant="primary" size="lg" onClick={scrollToRequestForm} data-id="element-1098" data-mp="2x4vbnsp5ukj2">
            {en ? 'Request service' : ru ? 'Запросить сервис' : 'Zatražite servis'}
          </Button>
        </div>
      </section>
    </main>;
}